import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface SelectMenuOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectMenuProps {
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectMenuOption[];
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
}

/**
 * A single-choice dropdown styled like the rest of the app. Native {@code <select>} popups are
 * drawn by the browser or OS and ignore CSS, so this follows the ARIA "select-only combobox"
 * pattern instead: arrows, Home/End and type-ahead move, Enter/Space choose, Escape and Tab close.
 */
export function SelectMenu({ label, value, options, onChange, disabled = false }: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  const labelId = `${id}-label`;
  const listId = `${id}-list`;
  const optionId = (index: number) => `${id}-option-${index}`;
  const selected = Math.max(0, options.findIndex((option) => option.value === value));

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node && root.current?.contains(event.target))) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    }
  });

  const show = (index: number) => {
    setActive(index);
    setOpen(true);
  };
  const choose = (index: number) => {
    const option = options[index];
    if (option !== undefined) {
      onChange(option.value);
    }
    setOpen(false);
    button.current?.focus();
  };
  /** Next option (after the current one, wrapping) whose label starts with {@code key}. */
  const typeAhead = (key: string, from: number) => {
    const wanted = key.toLowerCase();
    const order = options.map((_, offset) => (from + 1 + offset) % options.length);
    return order.find((index) => options[index]?.label.toLowerCase().startsWith(wanted));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1;
    const current = open ? active : selected;
    const move: Record<string, number> = {
      ArrowDown: Math.min(current + (open ? 1 : 0), last),
      ArrowUp: Math.max(current - (open ? 1 : 0), 0),
      Home: 0,
      End: last,
    };
    const target = move[event.key];
    if (target !== undefined) {
      event.preventDefault();
      show(target);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open) {
        choose(active);
      } else {
        show(selected);
      }
    } else if (event.key === 'Escape' && open) {
      event.stopPropagation(); // close the menu, not the drawer around it
      setOpen(false);
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const match = typeAhead(event.key, current);
      if (match !== undefined) {
        show(match);
      }
    }
  };

  return (
    <div className="select-menu" ref={root}>
      <label id={labelId} htmlFor={`${id}-button`}>
        {label}
      </label>
      <button
        ref={button}
        id={`${id}-button`}
        type="button"
        role="combobox"
        className="select-menu-button"
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? optionId(active) : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : show(selected))}
        onKeyDown={onKeyDown}
      >
        {options[selected]?.label ?? value}
      </button>
      {open && (
        <ul id={listId} role="listbox" aria-labelledby={labelId} className="select-menu-list">
          {options.map((option, index) => (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={option.value === value}
              data-active={index === active}
              onPointerDown={(event) => event.preventDefault()} // keep focus on the button
              onPointerMove={() => setActive(index)}
              onClick={() => choose(index)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
